/**
 * All service definitions.
 * The single source of truth for what HUN TECH offers.
 * No service data lives in components.
 */

export type Service = {
  readonly id: string
  readonly title: string
  readonly iconName: string
  readonly description: string
  readonly deliverables: readonly string[]
  readonly idealFor: string
}

export const services: readonly Service[] = [
  {
    id: 'web-development',
    title: 'Web Development',
    iconName: 'Code2',
    description:
      'We build fast, modern, and accessible web applications — from marketing sites to complex web platforms. Every project is custom-engineered, not templated.',
    deliverables: [
      'Custom websites and landing pages',
      'Web applications and dashboards',
      'E-commerce platforms',
      'CMS-powered content sites',
      'Progressive Web Apps (PWA)',
    ],
    idealFor: 'Businesses, startups, and personal brands needing a strong web presence.',
  },
  {
    id: 'mobile-apps',
    title: 'Mobile App Development',
    iconName: 'Smartphone',
    description:
      'Cross-platform mobile applications built for iOS and Android with native performance. One codebase — full platform coverage.',
    deliverables: [
      'iOS and Android apps',
      'Cross-platform (React Native / Flutter)',
      'Offline-capable apps',
      'Push notifications and real-time features',
      'App Store and Play Store deployment',
    ],
    idealFor: 'Businesses that need to reach users on mobile.',
  },
  {
    id: 'custom-software',
    title: 'Custom Software Systems',
    iconName: 'Settings2',
    description:
      'Purpose-built internal systems that match exactly how your business operates — not how a generic product forces you to operate.',
    deliverables: [
      'Enterprise Resource Planning (ERP)',
      'Human Resource Management (HRM)',
      'Inventory and supply chain systems',
      'School and hospital management systems',
      'Hotel and hospitality platforms',
    ],
    idealFor: 'Businesses with complex, specific operational workflows that off-the-shelf software cannot handle.',
  },
  {
    id: 'saas-products',
    title: 'SaaS Products & Modules',
    iconName: 'Layers',
    description:
      'We design and build multi-tenant SaaS products from the ground up. Your product idea, built right, with a scalable architecture from day one.',
    deliverables: [
      'Multi-tenant SaaS architecture',
      'Subscription billing and management',
      'Admin and analytics dashboards',
      'API-first design for integrations',
      'Scalable cloud deployment',
    ],
    idealFor: 'Entrepreneurs and businesses launching a software product for others to subscribe to.',
  },
  {
    id: 'it-consulting',
    title: 'IT Consulting',
    iconName: 'Lightbulb',
    description:
      'Strategic technology guidance — from architecture reviews to digital transformation planning. We help you make the right technical decisions before they become expensive mistakes.',
    deliverables: [
      'Tech stack architecture reviews',
      'Digital transformation roadmaps',
      'Code and security audits',
      'Team and process assessment',
      'Technology selection guidance',
    ],
    idealFor: 'Businesses navigating technology decisions or undergoing digital transformation.',
  },
] as const
